"use client";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { ArrowLeft, Clock, RefreshCw, Timer } from "lucide-react";
import { usePathname, useRouter } from "next/navigation";
import { useCallback, useEffect, useRef, useState, useTransition } from "react";

import { setFlightTimer } from "@/feature/flight/checkout/state/revalidate.slice";
import {
  refetch,
  setSearchKey,
} from "@/feature/flight/search/state/slices/flight-search-status.slice";

export function FlightSearchTimer() {
  const router = useRouter();
  const pathname = usePathname();
  const dispatch = useAppDispatch();

  const isLoading = useAppSelector(
    (state) => state.flightSearchStatus.isLoading,
  );
  const flightTimer = useAppSelector(
    (state) => state.flightRevalidate.flightTimer,
  );

  const [timeLeft, setTimeLeft] = useState<number | null>(() => {
    if (!flightTimer) return null;
    const diff = flightTimer - Date.now();
    return diff > 0 ? diff : null;
  });
  const [showDialog, setShowDialog] = useState(false);

  const [isPending, startTransition] = useTransition();
  const closeAfterPendingRef = useRef(false);
  // Tracks whether the timer was already expired when the component first saw it.
  // If so we silently clear it instead of popping the modal on the user.
  const timerAlreadyExpiredOnMountRef = useRef(false);

  const isRevalidatePage = pathname.startsWith("/flights/");

  useEffect(() => {
    if (!flightTimer) {
      timerAlreadyExpiredOnMountRef.current = false;
      return;
    }
    if (flightTimer - Date.now() <= 0) {
      timerAlreadyExpiredOnMountRef.current = true;
      // Timer already expired — clear it and invalidate cached searchKey
      // so SSE hook fetches fresh data when user returns to search page
      dispatch(setFlightTimer(undefined));
      dispatch(setSearchKey(undefined));
      return;
    }

    timerAlreadyExpiredOnMountRef.current = false;

    const tick = () => {
      const difference = flightTimer - Date.now();

      if (difference <= 0) {
        setTimeLeft(0);

        if (!timerAlreadyExpiredOnMountRef.current) {
          if (isRevalidatePage || !isLoading) {
            setShowDialog(true);
          }
        }
        return false;
      }

      setTimeLeft(difference);
      return true;
    };

    // First tick — runs inside setInterval callback (async), not sync in effect
    const interval = setInterval(() => {
      if (!tick()) clearInterval(interval);
    }, 1000);

    // Also run an immediate async tick via rAF so we don't wait 1s for first update
    requestAnimationFrame(() => {
      if (!tick()) clearInterval(interval);
    });

    return () => clearInterval(interval);
  }, [flightTimer, isLoading, isRevalidatePage, router, dispatch]);

  // Close dialog when transition finishes
  useEffect(() => {
    if (!closeAfterPendingRef.current || isPending) return;
    closeAfterPendingRef.current = false;
    // Delay to next frame to avoid sync setState in effect
    requestAnimationFrame(() => {
      setShowDialog(false);
    });
  }, [isPending]);

  const handleAction = useCallback(() => {
    closeAfterPendingRef.current = true;

    startTransition(() => {
      dispatch(setFlightTimer(undefined));

      if (isRevalidatePage) {
        // Go back to previous search page (preserves search params in history)
        dispatch(refetch(1));
        router.back();
      } else {
        // Trigger fresh search on the current page
        dispatch(refetch(1));
      }
    });
  }, [dispatch, isRevalidatePage, router]);

  if (!flightTimer || timeLeft === null) return null;

  const isExpired = timeLeft <= 0;

  const minutes = Math.floor(timeLeft / 60000);
  const seconds = Math.floor((timeLeft % 60000) / 1000);
  const formattedTime = `${minutes.toString().padStart(2, "0")}:${seconds
    .toString()
    .padStart(2, "0")}`;

  return (
    <>
      {!isExpired && (isRevalidatePage || !isLoading) && (
        <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground px-2 py-1 rounded-md">
          <Timer className="h-3.5 w-3.5" />
          <span className="tabular-nums">{formattedTime}</span>
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent
          className="sm:max-w-104"
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
          showCloseButton={false}
        >
          <div className="flex flex-col items-center text-center">
            <div className="flex size-12 items-center justify-center rounded-full bg-orange-100 dark:bg-orange-500/15">
              <Clock className="size-6 text-orange-600 dark:text-orange-400" />
            </div>

            <DialogHeader className="mt-4 space-y-2">
              <DialogTitle className="text-lg">
                {isRevalidatePage
                  ? "Fare Session Expired"
                  : "Search Session Expired"}
              </DialogTitle>
              <DialogDescription className="text-sm leading-relaxed text-muted-foreground">
                {isRevalidatePage
                  ? "This fare is no longer guaranteed. Prices may have changed since your last check. Please go back and select a flight again."
                  : "Your search results have expired. Fares and availability change frequently — please refresh to see the latest options."}
              </DialogDescription>
            </DialogHeader>

            <div className="mt-6 w-full">
              <Button
                onClick={handleAction}
                disabled={isPending}
                className="w-full gap-2"
                size="lg"
              >
                {isPending ? (
                  isRevalidatePage ? (
                    <>
                      <ArrowLeft className="size-4 animate-pulse" />
                      Going back...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="size-4 animate-spin" />
                      Refreshing...
                    </>
                  )
                ) : isRevalidatePage ? (
                  <>
                    <ArrowLeft className="size-4" />
                    Back to Search Results
                  </>
                ) : (
                  <>
                    <RefreshCw className="size-4" />
                    Refresh Search
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
